import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import SingleAutocompleteSelectField, {
  SingleAutocompleteChoiceType
} from "@saleor/components/SingleAutocompleteSelectField";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchProducts_search_edges_node } from "@saleor/searches/types/SearchProducts";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";
export interface FormData {
  products: SearchProducts_search_edges_node[];
  brands: SingleAutocompleteChoiceType[];
  query: string;
  onBrandChange(event: React.ChangeEvent<any>);
}

const useStyles = makeStyles(
  {
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    }
  },
  { name: "AssignProductDialog" }
);

export interface AssignProductDialogProps extends FetchMoreProps {
  brands: any;
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  products: SearchProducts_search_edges_node[];
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: SearchProducts_search_edges_node[]) => void;
  onSelectBrand: (value: string) => void;
  fetchBrands?: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;
}

function handleProductAssign(
  product: SearchProducts_search_edges_node,
  isSelected: boolean,
  selectedProducts: SearchProducts_search_edges_node[],
  setSelectedProducts: (data: SearchProducts_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedProducts(
      selectedProducts.filter(
        selectedProduct => selectedProduct.id !== product.id
      )
    );
  } else {
    setSelectedProducts([product]);
  }
}

function handleSelectAll(
  products: SearchProducts_search_edges_node[],
  setSelectedProducts: (data: SearchProducts_search_edges_node[]) => void
) {
  const getAll = [];
  products.forEach(product => {
    product.variants.forEach(variant => {
      if (
        !getAll.some(selectedVariants => selectedVariants.id === variant.id)
      ) {
        getAll.push({
          id: variant.id,
          productName: product.name
        });
      }
    });
  });
  setSelectedProducts(getAll);
}

const AssignProductDialog: React.FC<AssignProductDialogProps> = props => {
  const {
    brands,
    confirmButtonState,
    hasMore,
    open,
    loading,
    products,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit,
    onSelectBrand,
    fetchBrands,
    fetchMoreBrands
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [isSelectAll, setSelectAll] = React.useState(false);
  const [selectedProducts, setSelectedProducts] = React.useState<
    SearchProducts_search_edges_node[]
  >([]);
  const container = React.useRef<HTMLDivElement>();
  const handleSubmit = () => onSubmit(selectedProducts);
  const initialForm = {
    id: null,
    name: "",
    sellerBrand: ""
  };
  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");

  const sellerBrandChoices = maybe(
    () =>
      brands.map(brand => ({
        label: brand?.name + " - " + brand.legalName,
        value: brand?.id
      })),
    []
  );

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          sellerBrandChoices
        );

        let btnDisabled = false;
        if (!formData.sellerBrand) {
          btnDisabled = true;
        } else if (formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else if (!formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else {
          btnDisabled = true;
        }
        return (
          <Dialog
            onClose={onClose}
            open={open}
            classes={{ paper: scrollableDialogClasses.dialog }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Assign Product"
                description="dialog header"
              />
            </DialogTitle>
            <DialogContent
              className={scrollableDialogClasses.content}
              ref={container}
            >
              <SingleAutocompleteSelectField
                label={intl.formatMessage({
                  defaultMessage: "Brand:"
                })}
                name="sellerBrand"
                displayValue={sellerDisplayName}
                choices={sellerBrandChoices}
                disabled={false}
                value={formData.sellerBrand}
                onChange={e => {
                  handleSelectBrand(e);
                  onSelectBrand(e.target.value);
                }}
                fetchChoices={fetchBrands}
                {...fetchMoreBrands}
              />
              <FormSpacer />
              <TextField
                name="query"
                value={query}
                disabled={!formData.sellerBrand ? true : false}
                onChange={onQueryChange}
                label={intl.formatMessage({
                  defaultMessage: "Search Products"
                })}
                placeholder={intl.formatMessage({
                  defaultMessage:
                    "Search by product name, attribute, product type etc..."
                })}
                fullWidth
                InputProps={{
                  autoComplete: "off",
                  endAdornment: loading && <CircularProgress size={16} />
                }}
              />
              {formData.sellerBrand ? (
                <>
                  <FormSpacer />
                  <div
                    className={scrollableDialogClasses.scrollArea}
                    style={{ height: "70%" }}
                  >
                    <InfiniteScroll
                      pageStart={0}
                      loadMore={onFetchMore}
                      hasMore={hasMore}
                      useWindow={false}
                      loader={
                        <div
                          className={
                            scrollableDialogClasses.loadMoreLoaderContainer
                          }
                          key={0}
                        >
                          <CircularProgress size={16} />
                        </div>
                      }
                      threshold={10}
                    >
                      <ResponsiveTable key="table">
                        <TableBody>
                          <TableRow key="all">
                            <TableCell className={classes.colName}>
                              Select All
                            </TableCell>
                            <TableCell
                              padding="checkbox"
                              className={classes.checkboxCell}
                            >
                              <Checkbox
                                checked={isSelectAll}
                                onChange={e => {
                                  setSelectAll(!isSelectAll);
                                  if (e.target.checked) {
                                    handleSelectAll(
                                      products,
                                      setSelectedProducts
                                    );
                                  } else {
                                    setSelectedProducts([]);
                                  }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                          {products &&
                            products.map(product => {
                              const isSelected = selectedProducts.some(
                                selectedProduct =>
                                  selectedProduct.id === product.id
                              );

                              return (
                                <TableRow key={product.id}>
                                  <TableCell className={classes.colName}>
                                    {product.name}
                                  </TableCell>
                                  <TableCell
                                    padding="checkbox"
                                    className={classes.checkboxCell}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={e => {
                                        if (!e.target.checked) {
                                          setSelectAll(false);
                                        }
                                        handleProductAssign(
                                          product,
                                          isSelected,
                                          selectedProducts,
                                          setSelectedProducts
                                        );
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </ResponsiveTable>
                    </InfiniteScroll>
                    {products && products.length ? "" : "No Products Found"}
                  </div>
                </>
              ) : (
                ""
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                <FormattedMessage {...buttonMessages.back} />
              </Button>
              <ConfirmButton
                transitionState={confirmButtonState}
                color="primary"
                variant="contained"
                type="submit"
                onClick={submit}
                disabled={btnDisabled || selectedProducts.length === 0}
              >
                <FormattedMessage
                  defaultMessage="Assign products"
                  description="button"
                />
              </ConfirmButton>
            </DialogActions>
          </Dialog>
        );
      }}
    </Form>
  );
};
AssignProductDialog.displayName = "AssignProductDialog";
export default AssignProductDialog;
